/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { HttpCodesEnum } from '../enums/http.codes.enum';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogService, ModalService, NotifyService, UserService } from '../services';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { TokenStatus } from '../enums';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    constructor(
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private userService: UserService,
    ) {
    }

    /**
     * Return our request with a generated header including our JWT
     * @param req
     */
    public addToken(req: HttpRequest<any>): HttpRequest<any> {
        // Check our token status to generate our headers
        const tokenStatus = this.userService.tokenStatus();
        let headers = {};

        if (tokenStatus === TokenStatus.OK || tokenStatus === TokenStatus.REFRESH) {
            headers = {
                headers: req.headers.set(
                    'Authorization',
                    `Bearer ${this.userService.getUser().JWT}`
                )
            };
        }

        return req.clone(headers);
    }

    /**
     * Intercept our API request
     * @param req
     * @param next
     */
    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(this.addToken(req)).pipe(
            map(
                event => {
                    return event;
                }
            ),
            catchError(
                (err: HttpErrorResponse) => {
                    if (err instanceof HttpErrorResponse) {
                        this.logService.error('AuthInterceptor', err.message, {
                            requestBody: req.body,
                            responseBody: err.error
                        });
                        switch ((<HttpErrorResponse>err).status) {
                            case HttpCodesEnum.BAD_REQUEST:
                                return this.handleBadRequest(err);
                            case HttpCodesEnum.UNAUTHORIZED:
                                return this.handleUnauthorized(err);
                            case HttpCodesEnum.LOCKED:
                                return this.handleLocked(err);
                            default:
                                return observableThrowError(() => err);
                        }
                    }
                }
            )
        );
    }

    /**
     * Handle 400 errors
     * @param error
     * @private
     */
    private handleBadRequest(error: HttpErrorResponse): Observable<any> {
        // We no longer have a valid lease
        if (error?.status === 400 && error?.error?.error === 'invalid_grant') {
            this.modalService.openModalInParent('relogin');

            return observableThrowError(() => '');
        }

        return observableThrowError(() => error);
    }

    /**
     * Handle 401 errors
     * @param error
     * @private
     */
    private handleUnauthorized(error: HttpErrorResponse): Observable<any> {
        // TODO review when RBC is complete
        // this.router.navigateByUrl('access-denied')
        //     .then(() => {
        //         this.loadingService.setLoading(false);
        //     }, err => {
        //         this.log.error('auth.interceptor: handleUnauthorized', 'Error navigating', err);
        //     });
        return observableThrowError(() => error);
    }

    /**
     * Handle 423 errors
     * @param error
     * @private
     */
    private handleLocked(error: HttpErrorResponse): Observable<any> {
        this.notifyService.warning(error.message);

        return observableThrowError(() => error);
    }
}
